// Thin REST client for the Team Planner endpoints. Built on top of the
// shared api() wrapper so token refresh + JSON error shape stays
// consistent with the rest of the Hub.
import { api } from "../../../lib/api";
import type {
  Rock,
  Todo,
  Issue,
  Milestone,
  Subtask,
  TeamSummary,
  TeamMember,
  Comment,
  MeContext,
  PlannerStats,
  PlannerOwner,
  PlannerTeamSummary,
} from "../../../../../shared/team-planner/schemas";

const BASE = "/api/tools/team-planner";

// Extended milestone shape returned by `GET /milestones` — wraps the
// base Milestone with the parent-rock breadcrumb + primary owner +
// team summary so the cross-rock kanban can render each card without
// follow-up fetches.
export interface MilestoneCard extends Milestone {
  rockSeq: number;
  rockTitle: string;
  rockQuarter: string;
  parentTeamId: string | null;
  parentTeam: PlannerTeamSummary | null;
  primaryOwner: PlannerOwner | null;
}

// Server responses come back as JSON; dates arrive as ISO strings.
// hydrate* walks each row and converts the timestamp fields to Date so
// the rest of the UI can format them with the same helpers.
function reviveDates<T>(value: T, keys: readonly string[]): T {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map((v) => reviveDates(v, keys)) as T;
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    for (const k of keys) {
      const v = obj[k];
      if (typeof v === "string") obj[k] = new Date(v);
    }
  }
  return value;
}

const ROCK_DATE_KEYS = [
  "startAt", "committedAt", "dueAt", "createdAt", "updatedAt", "archivedAt",
] as const;
const TODO_DATE_KEYS = [
  "dueAt", "completedAt", "createdAt", "updatedAt",
] as const;
const ISSUE_DATE_KEYS = [
  "raisedAt", "resolvedAt", "createdAt", "updatedAt", "archivedAt",
] as const;
const MILESTONE_DATE_KEYS = [
  "dueAt", "completedAt", "createdAt", "updatedAt", "archivedAt",
] as const;
const SUBTASK_DATE_KEYS = [
  "dueAt", "completedAt", "createdAt", "updatedAt",
] as const;
const TEAM_DATE_KEYS = [
  "startDate", "endDate", "archivedAt", "createdAt",
] as const;
const COMMENT_DATE_KEYS = ["editedAt", "deletedAt", "createdAt"] as const;

export const tpApi = {
  // ── Me ──────────────────────────────────────────────────────────
  async meContext(): Promise<MeContext> {
    return api(`${BASE}/me/context`);
  },
  async stats(): Promise<PlannerStats> {
    return api(`${BASE}/me/stats`);
  },

  // ── Rocks ───────────────────────────────────────────────────────
  // `quarter` is the YYYY-Qn token (e.g. "2025-Q2") to pin the board
  // to a single quarter, or `null` to fetch every non-archived rock
  // the viewer can see across all quarters (powers the All toggle).
  async listRocks(quarter: string | null, scope: string): Promise<Rock[]> {
    const params = new URLSearchParams({ scope });
    if (quarter) params.set("quarter", quarter);
    const res = await api<{ rocks: Rock[] }>(`${BASE}/rocks?${params}`);
    return reviveDates(res.rocks, ROCK_DATE_KEYS);
  },
  async getRock(id: string): Promise<Rock> {
    const row = await api<Rock>(`${BASE}/rocks/by-id/${id}`);
    return reviveDates(row, ROCK_DATE_KEYS);
  },
  // Quarter-agnostic — used by pickers in MilestoneForm + TodoForm
  // where the user hasn't yet picked a quarter context. Mirrors the
  // Drillcut rocks.listAll procedure.
  async listAllRocks(): Promise<Rock[]> {
    const res = await api<{ rocks: Rock[] }>(`${BASE}/rocks/all`);
    return reviveDates(res.rocks, ROCK_DATE_KEYS);
  },
  async createRock(input: Record<string, unknown>): Promise<Rock> {
    const row = await api<Rock>(`${BASE}/rocks`, { method: "POST", json: input });
    return reviveDates(row, ROCK_DATE_KEYS);
  },
  async updateRock(id: string, input: Record<string, unknown>): Promise<Rock> {
    const row = await api<Rock>(`${BASE}/rocks/${id}`, { method: "PATCH", json: input });
    return reviveDates(row, ROCK_DATE_KEYS);
  },
  async setRockStatus(id: string, status: string): Promise<Rock> {
    const row = await api<Rock>(`${BASE}/rocks/${id}/status`, {
      method: "POST",
      json: { status },
    });
    return reviveDates(row, ROCK_DATE_KEYS);
  },
  async archiveRock(id: string): Promise<void> {
    await api(`${BASE}/rocks/${id}`, { method: "DELETE" });
  },

  // ── Todos ───────────────────────────────────────────────────────
  async listTodos(scope: string): Promise<Todo[]> {
    const res = await api<{ todos: Todo[] }>(
      `${BASE}/todos?scope=${encodeURIComponent(scope)}`,
    );
    return reviveDates(res.todos, TODO_DATE_KEYS);
  },
  async getTodo(id: string): Promise<Todo> {
    const row = await api<Todo>(`${BASE}/todos/${id}`);
    return reviveDates(row, TODO_DATE_KEYS);
  },
  async createTodo(input: Record<string, unknown>): Promise<Todo> {
    const row = await api<Todo>(`${BASE}/todos`, { method: "POST", json: input });
    return reviveDates(row, TODO_DATE_KEYS);
  },
  async updateTodo(id: string, input: Record<string, unknown>): Promise<Todo> {
    const row = await api<Todo>(`${BASE}/todos/${id}`, { method: "PATCH", json: input });
    return reviveDates(row, TODO_DATE_KEYS);
  },
  async setTodoStatus(id: string, status: string): Promise<Todo> {
    const row = await api<Todo>(`${BASE}/todos/${id}/status`, {
      method: "POST",
      json: { status },
    });
    return reviveDates(row, TODO_DATE_KEYS);
  },
  async archiveTodo(id: string): Promise<void> {
    await api(`${BASE}/todos/${id}`, { method: "DELETE" });
  },

  // ── Milestones ──────────────────────────────────────────────────
  async listMilestones(rockId: string): Promise<Milestone[]> {
    const res = await api<{ milestones: Milestone[] }>(
      `${BASE}/milestones/by-rock/${rockId}`,
    );
    return reviveDates(res.milestones, MILESTONE_DATE_KEYS);
  },
  // Cross-rock milestone list — powers the SPA's Milestones tab.
  // Each row carries the parent rock breadcrumb so kanban cards can
  // render the "R12 · Roll out CRM" chip without a second fetch.
  async listAllMilestones(
    scope: string,
    opts?: { status?: string },
  ): Promise<MilestoneCard[]> {
    const params = new URLSearchParams({ scope });
    if (opts?.status) params.set("status", opts.status);
    const res = await api<{ milestones: MilestoneCard[] }>(
      `${BASE}/milestones?${params}`,
    );
    return reviveDates(res.milestones, MILESTONE_DATE_KEYS);
  },
  async createMilestone(input: Record<string, unknown>): Promise<Milestone> {
    const row = await api<Milestone>(`${BASE}/milestones`, { method: "POST", json: input });
    return reviveDates(row, MILESTONE_DATE_KEYS);
  },
  async updateMilestone(id: string, input: Record<string, unknown>): Promise<Milestone> {
    const row = await api<Milestone>(`${BASE}/milestones/${id}`, { method: "PATCH", json: input });
    return reviveDates(row, MILESTONE_DATE_KEYS);
  },
  async deleteMilestone(id: string): Promise<void> {
    await api(`${BASE}/milestones/${id}`, { method: "DELETE" });
  },
  async listRockStatusUpdates(rockId: string) {
    const res = await api<{
      updates: Array<{ id: string; rag: string; note: string; postedAt: string; authorUserId: string }>;
    }>(`${BASE}/rocks/${rockId}/status-updates`);
    return res.updates.map((u) => ({ ...u, postedAt: new Date(u.postedAt) }));
  },
  async postRockStatusUpdate(rockId: string, body: { rag: string; note: string }) {
    return api(`${BASE}/rocks/${rockId}/status-updates`, { method: "POST", json: body });
  },

  // ── Subtasks ────────────────────────────────────────────────────
  async listSubtasks(todoId: string): Promise<Subtask[]> {
    const res = await api<{ subtasks: Subtask[] }>(
      `${BASE}/subtasks/by-todo/${todoId}`,
    );
    return reviveDates(res.subtasks, SUBTASK_DATE_KEYS);
  },
  async createSubtask(todoId: string, input: Record<string, unknown>): Promise<Subtask> {
    const row = await api<Subtask>(`${BASE}/subtasks/by-todo/${todoId}`, {
      method: "POST",
      json: input,
    });
    return reviveDates(row, SUBTASK_DATE_KEYS);
  },
  async updateSubtask(id: string, input: Record<string, unknown>): Promise<Subtask> {
    const row = await api<Subtask>(`${BASE}/subtasks/${id}`, { method: "PATCH", json: input });
    return reviveDates(row, SUBTASK_DATE_KEYS);
  },
  async deleteSubtask(id: string): Promise<void> {
    await api(`${BASE}/subtasks/${id}`, { method: "DELETE" });
  },

  // ── Issues / Opportunities ──────────────────────────────────────
  async listIssues(tab: string): Promise<Issue[]> {
    const res = await api<{ issues: Issue[] }>(
      `${BASE}/issues?tab=${encodeURIComponent(tab)}`,
    );
    return reviveDates(res.issues, ISSUE_DATE_KEYS);
  },
  async getIssue(id: string): Promise<Issue> {
    const row = await api<Issue>(`${BASE}/issues/by-id/${id}`);
    return reviveDates(row, ISSUE_DATE_KEYS);
  },
  async raiseIssue(input: Record<string, unknown>): Promise<Issue> {
    const row = await api<Issue>(`${BASE}/issues`, { method: "POST", json: input });
    return reviveDates(row, ISSUE_DATE_KEYS);
  },
  async updateIssue(id: string, input: Record<string, unknown>): Promise<Issue> {
    const row = await api<Issue>(`${BASE}/issues/${id}`, { method: "PATCH", json: input });
    return reviveDates(row, ISSUE_DATE_KEYS);
  },
  async setIssueStatus(id: string, status: string): Promise<Issue> {
    const row = await api<Issue>(`${BASE}/issues/${id}/status`, {
      method: "POST",
      json: { status },
    });
    return reviveDates(row, ISSUE_DATE_KEYS);
  },
  async voteIssue(id: string, action: "vote" | "unvote"): Promise<{ ok: boolean }> {
    return api(`${BASE}/issues/${id}/vote`, { method: "POST", json: { action } });
  },
  async convertIssueToTodo(id: string, input: Record<string, unknown>) {
    return api<{ todoId: string }>(`${BASE}/issues/${id}/convert-to-todo`, {
      method: "POST",
      json: input,
    });
  },
  async convertIssueToRock(id: string, input: Record<string, unknown>) {
    return api<{ rockId: string }>(`${BASE}/issues/${id}/convert-to-rock`, {
      method: "POST",
      json: input,
    });
  },
  async archiveIssue(id: string): Promise<void> {
    await api(`${BASE}/issues/${id}`, { method: "DELETE" });
  },

  // ── Teams ───────────────────────────────────────────────────────
  async listTeams(): Promise<TeamSummary[]> {
    const res = await api<{ teams: TeamSummary[] }>(`${BASE}/teams`);
    return reviveDates(res.teams, TEAM_DATE_KEYS);
  },
  async listTeamMembers(teamId: string): Promise<TeamMember[]> {
    const res = await api<{ members: TeamMember[] }>(`${BASE}/teams/${teamId}/members`);
    return res.members;
  },
  async teamDashboard(teamId: string) {
    return api<{
      team: TeamSummary;
      memberCount: number;
      rocksOnTrack: number;
      rocksOffTrack: number;
      rocksDone: number;
      todosOpen: number;
      todosDone: number;
      opportunitiesOpen: number;
      opportunitiesActionedThisMonth: number;
    }>(`${BASE}/teams/${teamId}/dashboard`);
  },
  async createTeam(input: Record<string, unknown>): Promise<TeamSummary> {
    return api(`${BASE}/teams`, { method: "POST", json: input });
  },
  async updateTeam(id: string, input: Record<string, unknown>): Promise<TeamSummary> {
    return api(`${BASE}/teams/${id}`, { method: "PATCH", json: input });
  },
  async teamMembershipChange(
    teamId: string,
    input: { userId: string; action: "add" | "remove" | "set_lead" | "unset_lead" },
  ): Promise<{ ok: boolean }> {
    return api(`${BASE}/teams/${teamId}/members`, { method: "POST", json: input });
  },

  // ── Participants ────────────────────────────────────────────────
  async setPrimaryOwner(
    itemKind: string,
    itemId: string,
    body: { subjectType: "user" | "team"; subjectId: string },
  ): Promise<{ ok: boolean }> {
    return api(`${BASE}/participants/${itemKind}/${itemId}/primary`, {
      method: "POST",
      json: body,
    });
  },
  async participantChange(
    itemKind: string,
    itemId: string,
    body: {
      role: "co_owner" | "watcher";
      subjectType: "user" | "team";
      subjectId: string;
      action: "add" | "remove";
    },
  ): Promise<{ ok: boolean }> {
    return api(`${BASE}/participants/${itemKind}/${itemId}`, {
      method: "POST",
      json: body,
    });
  },
  // Removes the primary_owner row entirely (the "Unassigned" state).
  // Used by the Owner-axis kanban DnD when a card is dropped into
  // the Unassigned column. setPrimaryOwner only swaps the row to a
  // new subject; this endpoint clears it instead.
  async clearPrimaryOwner(itemKind: string, itemId: string): Promise<{ ok: boolean }> {
    return api(`${BASE}/participants/${itemKind}/${itemId}/primary`, {
      method: "DELETE",
    });
  },

  // ── Comments ────────────────────────────────────────────────────
  async listComments(itemKind: string, itemId: string): Promise<Comment[]> {
    const res = await api<{ comments: Comment[] }>(
      `${BASE}/comments/${itemKind}/${itemId}`,
    );
    return reviveDates(res.comments, COMMENT_DATE_KEYS);
  },
  async postComment(
    itemKind: string,
    itemId: string,
    body: string,
    mentions: string[] = [],
  ): Promise<Comment> {
    const row = await api<Comment>(`${BASE}/comments/${itemKind}/${itemId}`, {
      method: "POST",
      json: { body, mentions },
    });
    return reviveDates(row, COMMENT_DATE_KEYS);
  },

  // ── Health ──────────────────────────────────────────────────────
  async health(scope: string) {
    return api<{
      teams: Array<{
        team: { id: string; kind: string; name: string; mono: string; color: string | null; mine: boolean };
        rocks: Record<string, unknown>;
        todos: Record<string, unknown>;
        opps: Record<string, unknown>;
        overall: "green" | "amber" | "red" | "neutral";
      }>;
      rollup: Record<string, number>;
      generatedAt: string;
      scope: string;
    }>(`${BASE}/health?scope=${encodeURIComponent(scope)}`);
  },

  // ── Users ───────────────────────────────────────────────────────
  // Search rows carry a `pendingProjection` flag set to true when the
  // hit came from Microsoft Graph but doesn't yet have a planner_user
  // row. The PersonSelect picker calls `projectUser` on first
  // selection to upsert the row + receive the planner uuid that
  // participant tables FK to.
  async searchUsers(q: string): Promise<Array<PlannerOwner & { pendingProjection: boolean }>> {
    const res = await api<{
      users: Array<PlannerOwner & { pendingProjection: boolean }>;
    }>(`${BASE}/users?q=${encodeURIComponent(q)}`);
    return res.users;
  },
  async projectUser(
    payload: { entraObjectId: string; displayName: string | null; email: string | null },
  ): Promise<PlannerOwner> {
    return api(`${BASE}/users/projection`, { method: "POST", json: payload });
  },
  // Admin-only diag. Returns the live Graph configuration state so
  // an operator can see at a glance why PersonSelect is returning
  // empty hits without tailing App Service logs.
  async graphDiag(): Promise<{
    config: {
      configured: boolean;
      hasTenantId: boolean;
      hasClientId: boolean;
      hasClientSecret: boolean;
    };
    localUserCount: number;
    testSearch: null | {
      ok: boolean;
      hitCount?: number;
      error?: { status?: number; code?: string; message: string };
    };
  }> {
    return api(`${BASE}/users/diag`);
  },

  // Admin-only diag. Returns the live state of the notification
  // fan-out — env vars, migration, bot install, the calling admin's
  // notify prefs, last 5 activity events they triggered. Lets an
  // operator see at a glance where the chain is breaking.
  async notifyDiag(): Promise<{
    bot: {
      configured: boolean;
      config: {
        hasAppId: boolean;
        hasAppPassword: boolean;
        appType: string;
        hasTenantId: boolean;
      };
      messagesEndpoint: string | null;
      tableExists: boolean;
      totalConversations: number;
    };
    email: {
      configured: boolean;
      config: {
        hasFromAddress: boolean;
        hasGraphTenant: boolean;
        hasGraphClient: boolean;
        hasGraphSecret: boolean;
      };
    };
    viewer: {
      plannerUserId: string;
      displayName: string | null;
      email: string | null;
      botConversation: null | {
        capturedAt: string;
        lastSentAt: string | null;
        serviceUrl: string;
        tenantId: string;
      };
      notifyPrefs: {
        defaulted?: boolean;
        notifyViaTeams: boolean;
        notifyViaEmail: boolean;
        digestMode: string;
        quietHoursStart?: string | null;
        quietHoursEnd?: string | null;
        timezone?: string | null;
      };
      recentActivity: Array<{
        verb: string;
        itemKind: string;
        itemId: string;
        createdAt: string;
      }>;
    };
  }> {
    return api(`${BASE}/health/notify-diag`);
  },

  async notifyTest(): Promise<{
    teams:
      | { ok: true }
      | { ok: false; reason: string; code?: string; status?: number };
    email:
      | { ok: true }
      | { ok: false; reason?: string; status?: number };
  }> {
    return api(`${BASE}/health/notify-test`, { method: "POST" });
  },

  // ── Activity feed ───────────────────────────────────────────────
  async listActivity(itemKind: string, itemId: string) {
    const res = await api<{
      events: Array<{
        id: string;
        itemKind: string;
        itemId: string;
        actorUserId: string;
        actorDisplayName: string | null;
        verb: string;
        payload: Record<string, unknown> | null;
        createdAt: string;
      }>;
    }>(`${BASE}/activity/${itemKind}/${itemId}`);
    return res.events.map((e) => ({ ...e, createdAt: new Date(e.createdAt) }));
  },
  async recentActivity() {
    const res = await api<{
      events: Array<{
        id: string;
        itemKind: string;
        itemId: string;
        itemTitle: string | null;
        parentItemId: string | null;
        actorUserId: string;
        actorDisplayName: string | null;
        verb: string;
        payload: Record<string, unknown> | null;
        createdAt: string;
      }>;
    }>(`${BASE}/activity/recent`);
    return res.events.map((e) => ({ ...e, createdAt: new Date(e.createdAt) }));
  },
  async listMentions() {
    const res = await api<{
      mentions: Array<{
        id: string;
        itemKind: string;
        itemId: string;
        authorUserId: string;
        authorDisplayName: string | null;
        body: string;
        createdAt: string;
      }>;
    }>(`${BASE}/activity/mentions`);
    return res.mentions.map((m) => ({ ...m, createdAt: new Date(m.createdAt) }));
  },

  // ── Notification preferences ────────────────────────────────────
  async getPreferences() {
    return api<{
      notifyOnAssignment: boolean;
      notifyOnMention: boolean;
      notifyOnStatusChange: boolean;
      notifyOnMilestoneComplete: boolean;
      notifyOnIssueRaisedInMyTeam: boolean;
      notifyOnDueSoon: boolean;
      notifyOnOverdue: boolean;
      notifyViaEmail: boolean;
      notifyViaTeams: boolean;
      notifyForOwnChanges: boolean;
      digestMode: "immediate" | "daily" | "weekly";
      quietHoursStart: string | null;
      quietHoursEnd: string | null;
      timezone: string | null;
    }>(`${BASE}/preferences`);
  },
  async updatePreferences(patch: Record<string, unknown>) {
    return api(`${BASE}/preferences`, { method: "PATCH", json: patch });
  },
};
